import { useEffect, useMemo, useRef, useState } from 'react'
import { useToast } from '../Toast'
import { useI18n } from '../../utils/i18n'
import { uploadFileOrFallback } from '../../utils/upload'
import { useResolvedUrl } from '../../utils/useResolvedUrl'
import { audioBufferToWav, decodeAudioWaveform } from './audioUtils'
import { useAudioSplitterPersistence } from './useAudioSplitterPersistence'
import { useTagDragging } from './useTagDragging'

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || ''
const UPLOAD_API = API_ORIGIN ? `${API_ORIGIN.replace(/\/$/, '')}/api/upload` : '/api/upload'

export function useAudioSplitter({ sourceAudio, existingSegments, onSegmentsGenerated }) {
  const { tx } = useI18n()
  const { toast } = useToast()
  const [audioUrl, setAudioUrl] = useState(sourceAudio || '')
  const resolvedAudioUrl = useResolvedUrl(audioUrl)
  const [audioData, setAudioData] = useState(null) // ArrayBuffer for trimming
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [waveform, setWaveform] = useState([])
  const [tags, setTags] = useState([]) // 切分点列表 [{time: 秒, hasImage: bool, imageUrl: ''}]
  const [uploading, setUploading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [draggingTagIndex, setDraggingTagIndex] = useState(null) // 正在拖拽的切分点
  const MIN_GAP_SEC = 0.5
  const WARN_LONG_SEC = 15
  const OVERLONG_SEC = 30
  const [autoSplitTargetSec, setAutoSplitTargetSec] = useState(12)
  const [imageUploadingIndex, setImageUploadingIndex] = useState(null)
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0)
  const [segmentPreviewPlaying, setSegmentPreviewPlaying] = useState(false)
  const [markerMode, setMarkerMode] = useState(false) // false=切分点模式, true=段内标记模式
  const [segmentMarkers, setSegmentMarkers] = useState({}) // { [segmentIndex]: [{time:number, imageUrl:string}] }

  useAudioSplitterPersistence({
    sourceAudio, existingSegments, audioUrl, setAudioUrl, tags, setTags,
    segmentMarkers, setSegmentMarkers, markerMode, setMarkerMode,
    autoSplitTargetSec, setAutoSplitTargetSec, selectedSegmentIndex,
    setSelectedSegmentIndex,
  })

  const audioRef = useRef(null)
  const fileInputRef = useRef(null)
  const imageInputRef = useRef(null)
  const imageTargetIndexRef = useRef(null)
  const audioContextRef = useRef(null)
  const waveformRef = useRef(null)
  const imageTargetTypeRef = useRef('tag') // 'tag' | 'marker'

  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.src = resolvedAudioUrl || audioUrl
      loadWaveform(audioUrl)
    }
  }, [audioUrl, resolvedAudioUrl])

  const segmentMeta = useMemo(() => {
    if (!duration || tags.length === 0) return []
    return tags.map((tag, i) => {
      const start = tag.time
      const end = i < tags.length - 1 ? tags[i + 1].time : duration
      return { index: i, start, end, dur: Math.max(0, end - start) }
    })
  }, [tags, duration])

  useEffect(() => {
    if (!segmentMeta.length) return
    if (selectedSegmentIndex >= segmentMeta.length) setSelectedSegmentIndex(segmentMeta.length - 1)
  }, [segmentMeta.length])

  const selectedSegment = segmentMeta[selectedSegmentIndex]
  const selectedSegmentMarkers = useMemo(() => {
    return segmentMarkers[selectedSegmentIndex] || []
  }, [segmentMarkers, selectedSegmentIndex])

  const hasOverlong = useMemo(() => segmentMeta.some((s) => s.dur > OVERLONG_SEC), [segmentMeta])

  const insertTagAt = (time) => {
    if (!duration) return
    const t = Math.max(0, Math.min(duration, time))
    setTags((prev) => {
      const sorted = [...prev].sort((a, b) => a.time - b.time)
      const nearest = sorted.find((x) => Math.abs(x.time - t) < 0.2)
      if (nearest) return prev

      // 找到插入位置并 clamp 在相邻 tag 之间
      let insertAt = t
      for (let i = 0; i < sorted.length; i++) {
        const left = sorted[i]
        const right = sorted[i + 1]
        const leftTime = left.time
        const rightTime = right ? right.time : duration
        if (insertAt >= leftTime && insertAt <= rightTime) {
          const minT = leftTime + MIN_GAP_SEC
          const maxT = rightTime - MIN_GAP_SEC
          if (maxT <= minT) return prev
          insertAt = Math.max(minT, Math.min(maxT, insertAt))
          break
        }
      }
      const next = [...sorted, { time: insertAt, hasImage: false, imageUrl: '' }].sort((a, b) => a.time - b.time)
      return next
    })
  }

  const splitSegmentAtMid = (segmentIndex) => {
    const seg = segmentMeta.find((s) => s.index === segmentIndex)
    if (!seg) return
    insertTagAt((seg.start + seg.end) / 2)
  }

  const autoSplitOverlong = (targetSec, maxIterations = 50) => {
    if (!duration) return
    let iterations = 0
    const runOnce = () => {
      const meta = segmentMeta
      const over = meta.find((s) => s.dur > Math.max(OVERLONG_SEC, targetSec))
      if (!over) return false
      // 按 targetSec 均分：插入多个点
      const parts = Math.ceil(over.dur / targetSec)
      if (parts <= 1) return false
      const step = over.dur / parts
      for (let i = 1; i < parts; i++) {
        insertTagAt(over.start + step * i)
      }
      return true
    }
    while (iterations < maxIterations && runOnce()) {
      iterations++
    }
  }

  const loadWaveform = async (url) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
      }
      const decoded = await decodeAudioWaveform(url, audioContextRef.current)
      setAudioData(decoded.audioData)
      setWaveform(decoded.waveform)
      setDuration(decoded.duration)
      if (tags.length === 0) setTags([{ time: 0, hasImage: false, imageUrl: '' }])
    } catch (error) {
      console.error('Failed to load waveform:', error)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const t = audioRef.current.currentTime
      setCurrentTime(t)
      if (segmentPreviewPlaying && selectedSegment && t >= selectedSegment.end) {
        stopSelectedSegment()
      }
    }
  }

  const togglePlay = () => {
    if (!audioRef.current) return
    setSegmentPreviewPlaying(false)
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const handleUpload = async (file) => {
    if (!file.type.startsWith('audio/')) {
      toast({
        kind: 'warning',
        title: tx({ zh: '提示', en: 'Hint' }),
        message: tx({ zh: '请选择音频文件', en: 'Please choose an audio file' }),
      })
      return
    }

    setUploading(true)
    try {
      const { url } = await uploadFileOrFallback(file, UPLOAD_API)
      setAudioUrl(url)
      setTags([{ time: 0, hasImage: false, imageUrl: '' }])
    } catch (e) {
      console.error('Upload failed:', e)
    }
    setUploading(false)
  }

  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast({
        kind: 'warning',
        title: tx({ zh: '提示', en: 'Hint' }),
        message: tx({ zh: '请选择图片文件', en: 'Please choose an image file' }),
      })
      return null
    }
    try {
      const { url } = await uploadFileOrFallback(file, UPLOAD_API)
      return url
    } catch (e) {
      console.error('Image upload failed:', e)
      toast({
        kind: 'error',
        title: tx({ zh: '图片上传失败', en: 'Image upload failed' }),
        message: String(e?.message || e),
      })
    }
    return null
  }

  const openImagePickerForTag = (index) => {
    imageTargetTypeRef.current = 'tag'
    imageTargetIndexRef.current = index
    imageInputRef.current?.click()
  }

  const openImagePickerForMarker = (markerIndex) => {
    imageTargetTypeRef.current = 'marker'
    imageTargetIndexRef.current = markerIndex
    imageInputRef.current?.click()
  }

  const addMarkerAt = (time) => {
    if (!selectedSegment) return
    const t = Math.max(selectedSegment.start + MIN_GAP_SEC, Math.min(selectedSegment.end - MIN_GAP_SEC, time))
    let markerIndex = 0
    setSegmentMarkers((prev) => {
      const list = [...(prev[selectedSegmentIndex] || [])]
      markerIndex = list.length
      list.push({ time: t, imageUrl: '' })
      return { ...prev, [selectedSegmentIndex]: list }
    })
    // 打完点立刻弹出选图
    setTimeout(() => openImagePickerForMarker(markerIndex), 0)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleUpload(files[0])
    }
  }

  // 点击波形添加切分点
  const handleWaveformClick = (e) => {
    if (!duration || draggingTagIndex !== null) return
    const rect = waveformRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percent = x / rect.width
    const time = percent * duration

    // 段内标记模式：点击在当前段内添加图片标记点
    if (markerMode && selectedSegment) {
      if (time < selectedSegment.start || time > selectedSegment.end) {
        // 点击段外就切换到最近段（更符合直觉）
        const idx = segmentMeta.findIndex((s) => time >= s.start && time <= s.end)
        if (idx >= 0) setSelectedSegmentIndex(idx)
        return
      }
      addMarkerAt(time)
      return
    }
    
    // 检查是否点击在已有 tag 附近（0.5秒内），如果是则跳转播放位置
    const nearTag = tags.find(t => Math.abs(t.time - time) < 0.5)
    if (nearTag) {
      if (audioRef.current) {
        audioRef.current.currentTime = nearTag.time
        setCurrentTime(nearTag.time)
      }
      return
    }
    
    // 添加新 tag
    const newTags = [...tags, { time, hasImage: false, imageUrl: '' }]
    newTags.sort((a, b) => a.time - b.time)
    setTags(newTags)
  }

  // 拖拽切分点
  const handleTagDragStart = (e, index) => {
    e.preventDefault()
    e.stopPropagation()
    if (index === 0) return // 第一个点固定在0
    setDraggingTagIndex(index)
  }

  useTagDragging({
    draggingTagIndex, tags, duration, waveformRef, setTags, setDraggingTagIndex,
  })

  // 删除切分点
  const removeTag = (index) => {
    if (index === 0) return // 不能删除第一个
    setTags(tags.filter((_, i) => i !== index))
  }

  // 更新 tag 的图片设置
  const updateTag = (index, updates) => {
    setTags(tags.map((t, i) => i === index ? { ...t, ...updates } : t))
  }

  // 精确调整 tag 时间
  const adjustTagTime = (index, newTime) => {
    if (index === 0) {
      // 第一个 tag 固定在 0
      return
    }
    const prevTime = index > 0 ? tags[index - 1].time + 0.1 : 0
    const nextTime = index < tags.length - 1 ? tags[index + 1].time - 0.1 : duration
    const clampedTime = Math.max(prevTime, Math.min(newTime, nextTime))
    
    const newTags = [...tags]
    newTags[index] = { ...newTags[index], time: clampedTime }
    setTags(newTags)
  }

  // 跳转到 tag 位置播放
  const seekToTag = (time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time
      setCurrentTime(time)
    }
  }

  const playSelectedSegment = () => {
    if (!audioRef.current || !selectedSegment) return
    audioRef.current.currentTime = selectedSegment.start
    audioRef.current.play()
    setIsPlaying(true)
    setSegmentPreviewPlaying(true)
  }

  const stopSelectedSegment = () => {
    if (!audioRef.current) return
    audioRef.current.pause()
    setIsPlaying(false)
    setSegmentPreviewPlaying(false)
  }

  // 格式化时间
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    const ms = Math.floor((seconds % 1) * 10)
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`
  }

  // 切分音频并生成段落
  const handleSplit = async () => {
    if (!audioData || tags.length < 1) return
    
    setProcessing(true)
    try {
      const audioBuffer = await audioContextRef.current.decodeAudioData(audioData.slice(0))
      const sampleRate = audioBuffer.sampleRate
      const segments = []
      
      for (let i = 0; i < tags.length; i++) {
        const startTime = tags[i].time
        const endTime = i < tags.length - 1 ? tags[i + 1].time : duration
        const segDuration = endTime - startTime
        
        // 跳过太短的段落
        if (segDuration < 0.5) continue
        
        const startSample = Math.floor(startTime * sampleRate)
        const endSample = Math.floor(endTime * sampleRate)
        const length = endSample - startSample
        
        // 创建新的 AudioBuffer
        const newBuffer = audioContextRef.current.createBuffer(
          audioBuffer.numberOfChannels,
          length,
          sampleRate
        )
        
        for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
          const oldData = audioBuffer.getChannelData(channel)
          const newData = newBuffer.getChannelData(channel)
          for (let j = 0; j < length; j++) {
            newData[j] = oldData[startSample + j]
          }
        }
        
        // 转换为 WAV
        const wavBlob = audioBufferToWav(newBuffer)
        
        // 上传
        const segmentFile = new File([wavBlob], `segment_${i + 1}.wav`, { type: 'audio/wav' })
        const { url: segmentUrl } = await uploadFileOrFallback(segmentFile, UPLOAD_API)
        if (segmentUrl) {
          const markers = (segmentMarkers[i] || []).map((m) => ({
            time: m.time, // absolute time on source audio
            image: m.imageUrl,
          }))
          segments.push({
            text: tx({ zh: '段落 {n}', en: 'Segment {n}' }, { n: i + 1 }),
            audio_url: segmentUrl,
            image: tags[i].hasImage ? tags[i].imageUrl : '',
            markers,
            // 记录原始时间信息，方便后续重新编辑
            start_time: startTime,
            end_time: endTime,
            duration_ms: Math.round(segDuration * 1000),
            frame_duration_ms: 500,
          })
        }
      }
      
      // 回调：返回源音频 + 带时间信息的段落
      if (onSegmentsGenerated && segments.length > 0) {
        onSegmentsGenerated({
          source_audio: audioUrl,  // 原始完整音频
          segments: segments,
        })
      }
      
      toast({
        kind: 'success',
        title: tx({ zh: '切分完成', en: 'Split complete' }),
        message: tx({ zh: '成功切分为 {n} 个段落。', en: 'Split into {n} segments.' }, { n: segments.length }),
      })
    } catch (e) {
      console.error('Split failed:', e)
      toast({ kind: 'error', title: tx({ zh: '切分失败', en: 'Split failed' }), message: String(e?.message || e) })
    }
    setProcessing(false)
  }

  const handleClear = () => {
    setAudioUrl('')
    setAudioData(null)
    setWaveform([])
    setDuration(0)
    setCurrentTime(0)
    setTags([])
  }

  // 空格键播放/暂停
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && audioUrl) {
        e.preventDefault()
        togglePlay()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [audioUrl, isPlaying])


  return {
    OVERLONG_SEC, WARN_LONG_SEC, adjustTagTime, audioRef, audioUrl,
    autoSplitOverlong, autoSplitTargetSec, currentTime, draggingTagIndex, duration,
    fileInputRef, formatTime, handleClear, handleDrop, handleLoadedMetadata,
    handleSplit, handleTagDragStart, handleTimeUpdate, handleUpload,
    handleWaveformClick, hasOverlong, imageInputRef, imageTargetIndexRef,
    imageTargetTypeRef, imageUploadingIndex, isDragging, isPlaying, markerMode,
    openImagePickerForMarker, openImagePickerForTag, playSelectedSegment,
    processing, removeTag, seekToTag, segmentPreviewPlaying, selectedSegment,
    selectedSegmentIndex, selectedSegmentMarkers, setAutoSplitTargetSec,
    setImageUploadingIndex, setIsDragging, setIsPlaying, setMarkerMode,
    setSegmentMarkers, setSelectedSegmentIndex, setTags, splitSegmentAtMid,
    stopSelectedSegment, tags, togglePlay, tx, updateTag, uploadImage, uploading,
    waveform, waveformRef,
  }
}
